import React, { ReactNode, forwardRef, useCallback } from "react"
import {
  FormControl,
  Typography,
  InputLabel,
  MenuItem,
  Select as MUISelect,
  Stack,
  FormHelperText,
  MenuProps,
  InputProps
} from "@mui/material"
import { unstable_useId as useId } from "@mui/material/utils"
import Chip from "../Chip"

import { useStyles } from "./styles"
import { FaIcon } from "../../../../mp-core/icons"

const SelectIcon: React.FC<unknown> = props => (
  <FaIcon solid name="caret-down" {...props} />
)

const LoadingIcon: React.FC<unknown> = props => (
  <FaIcon solid spin name="spinner-third" {...props} />
)

export type SelectOptionType = {
  label: string | React.ReactElement
  value: string
  disabled?: boolean
  startIcon?: ReactNode
  "data-automation-id"?: string
}

type Props = {
  id?: string
  name?: string
  required?: boolean
  inline?: boolean
  label?: React.ReactNode
  emptyPlaceholder?: string
  allowDeselect?: boolean
  showDeselectOption?: boolean
  deselectOptionName?: ReactNode
  disabled?: boolean
  multiple?: boolean
  showLine?: boolean
  value?: string | string[]
  open?: boolean
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    child: React.ReactNode
  ) => void
  onOpen?: () => void
  onClose?: () => void
  onDeleteItem?: (selectedItem: string) => void
  renderValue?: (value: string | string[]) => string | React.ReactElement
  options?: SelectOptionType[]
  size?: "medium" | "small"
  variant?: "outlined" | "filled"
  error?: boolean
  className?: string
  menuClassName?: string
  optionClassName?: string
  menuContainerClassName?: string
  fullWidth?: boolean
  isLoading?: boolean
  startIcon?: ReactNode
  helperText?: ReactNode
  MenuProps?: Partial<MenuProps> & { "data-automation-id"?: string }
  inputProps?: Partial<InputProps["inputProps"]> & {
    "data-automation-id"?: string
  }
  "data-automation-id"?: string
  children?: React.ReactNode
  arrowComponent?: React.FC<unknown>
  onOptionClick?: (option: SelectOptionType) => void
  useComboboxRole?: boolean
}

export const CustomSelectClasses = {
  renderValueContainer: "renderValueContainer"
}

const Select = forwardRef<any, Props>(
  (
    {
      id: idProp,
      name,
      required = false,
      inline = false,
      label,
      emptyPlaceholder,
      allowDeselect = false,
      showDeselectOption = true,
      deselectOptionName = <em>None</em>,
      disabled = false,
      multiple = false,
      showLine = inline,
      value,
      open,
      onChange,
      onDeleteItem,
      renderValue,
      options = [],
      size = "medium",
      error = false,
      variant = "outlined",
      className: outerClasses,
      menuClassName,
      optionClassName,
      menuContainerClassName,
      startIcon,
      isLoading = false,
      fullWidth = false,
      helperText,
      MenuProps = {},
      inputProps = {},
      "data-automation-id": dataAutomationId,
      children,
      arrowComponent,
      onOptionClick,
      useComboboxRole = false,
      ...props
    },
    ref
  ) => {
    // a11y: MUI builds the trigger's aria-labelledby from this id, and IDREFs
    // resolve to the first match — a shared fallback named every Select on the
    // page after whichever one rendered first.
    const id = useId(idProp)

    const { classes, cx } = useStyles({
      inline,
      label: !!label,
      error,
      variant,
      showLine
    })

    const isSelected = useCallback(
      (option: SelectOptionType) => {
        if (multiple) {
          return (
            Array.isArray(value) &&
            value.some(v => String(v) === String(option.value))
          )
        }
        return String(option.value) === String(value)
      },
      [multiple, value]
    )

    // `disableUnderline` prop can be only with `variant={filled}`,
    // in other case we have warning message
    const disableUnderlineProp =
      variant === "filled" ? { disableUnderline: true } : {}

    // a11y: only reference the label when one is actually rendered below;
    // otherwise the trigger is named by its own visible text.
    const selectLabelId = label ? `${id}-label` : undefined
    // need id for accessibility of helper text
    const helperTextId = helperText ? `${id}-helper-text` : null

    return (
      <FormControl
        className={cx(classes.container, outerClasses)}
        fullWidth={fullWidth}
        aria-invalid={!!error}
        required={required}
      >
        {label && (
          <InputLabel
            required={required}
            id={selectLabelId}
            shrink={false}
            className={classes.label}
            disabled={disabled}
            size={size === "medium" ? "normal" : "small"}
          >
            {label}
          </InputLabel>
        )}

        <MUISelect
          required={required}
          ref={ref}
          id={id}
          labelId={selectLabelId}
          name={name}
          className={cx(classes.select, { [classes.ariaDisabled]: isLoading })}
          size={size}
          variant={variant}
          disabled={disabled}
          multiple={multiple}
          value={value || ""}
          {...(open !== undefined && { open: isLoading ? false : open })}
          displayEmpty={allowDeselect}
          onChange={isLoading ? undefined : onChange}
          MenuProps={{
            className: cx(classes.menu, menuClassName),
            classes: { paper: menuContainerClassName },
            ...MenuProps
          }}
          SelectDisplayProps={{
            ...(useComboboxRole && { role: "combobox" }),
            // a11y: with no label MUI points aria-labelledby at the trigger's own
            // id; VoiceOver won't derive a name from that self-reference, so drop
            // it and let the visible text (placeholder or value) name the control.
            ...(!label && { "aria-labelledby": undefined }),
            ...(isLoading && {
              "aria-disabled": true,
              // a11y: pointerEvents:none already blocks mouse; this blocks the
              // matching keyboard activation so the menu can't open during load.
              onKeyDown: (event: React.KeyboardEvent) => {
                if (
                  event.key === "Enter" ||
                  event.key === " " ||
                  event.key === "ArrowDown" ||
                  event.key === "ArrowUp"
                ) {
                  event.preventDefault()
                }
              }
            })
          }}
          inputProps={{
            "aria-labelledby": selectLabelId,
            "aria-describedby": helperTextId,
            ...inputProps
          }}
          IconComponent={isLoading ? LoadingIcon : arrowComponent ?? SelectIcon}
          {...disableUnderlineProp}
          renderValue={value => {
            if (!!renderValue) return renderValue(value)

            if (multiple && Array.isArray(value)) {
              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                  pt={1}
                  className={CustomSelectClasses.renderValueContainer}
                >
                  {value.map(item => {
                    const label =
                      options.find(option => option.value === item)?.label ??
                      item

                    return (
                      <Chip
                        key={item}
                        label={label}
                        size="xs"
                        variant="outlined"
                        onDelete={() => onDeleteItem(item)}
                        onMouseDown={event => event.stopPropagation()}
                      />
                    )
                  })}
                </Stack>
              )
            }

            if (allowDeselect && !value && !!emptyPlaceholder) {
              return (
                <span
                  className={cx(
                    classes.placeholder,
                    CustomSelectClasses.renderValueContainer
                  )}
                >
                  {emptyPlaceholder}
                </span>
              )
            }

            const valueComponent = (
              <Typography className={classes.selectValue}>
                {options.find(option => option.value === value)?.label ??
                  emptyPlaceholder}
              </Typography>
            )

            if (startIcon) {
              return (
                <Stack
                  flexDirection="row"
                  gap={4}
                  alignItems="center"
                  className={CustomSelectClasses.renderValueContainer}
                >
                  {startIcon}

                  {valueComponent}
                </Stack>
              )
            }

            return valueComponent
          }}
          data-automation-id={dataAutomationId}
          {...props}
        >
          {children
            ? children
            : [
                allowDeselect && showDeselectOption && (
                  <MenuItem
                    key="none"
                    value=""
                    className={cx(classes.option, optionClassName)}
                    data-automation-id={
                      !!dataAutomationId
                        ? `${dataAutomationId}-option-none`
                        : "select-option-none"
                    }
                  >
                    {deselectOptionName}
                  </MenuItem>
                ),
                ...options.map(option => {
                  const selected = isSelected(option)

                  return (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      onClick={() => !isLoading && onOptionClick?.(option)}
                      data-parent-id={id}
                      className={cx(classes.option, optionClassName)}
                      disabled={!!option.disabled && option.value !== value}
                      aria-selected={selected}
                      data-automation-id={
                        option["data-automation-id"] || !!dataAutomationId
                          ? `${dataAutomationId}-option`
                          : "select-option"
                      }
                    >
                      {!!option.startIcon ? (
                        <Stack spacing={4} direction="row" alignItems="center">
                          {option.startIcon}

                          <span>{option.label}</span>
                        </Stack>
                      ) : (
                        option.label
                      )}
                      {selected && (
                        <span
                          style={{
                            position: "absolute",
                            width: "1px",
                            height: "1px",
                            padding: 0,
                            margin: "-1px",
                            overflow: "hidden",
                            clip: "rect(0, 0, 0, 0)",
                            whiteSpace: "nowrap",
                            borderWidth: 0
                          }}
                          aria-hidden="false"
                        >
                          {" selected"}
                        </span>
                      )}
                    </MenuItem>
                  )
                }),
                !options.length && (
                  <MenuItem
                    key="no-options"
                    disabled
                    className={cx(classes.option, optionClassName)}
                    data-automation-id={
                      !!dataAutomationId
                        ? `${dataAutomationId}-no-option`
                        : "select-no-option"
                    }
                  >
                    <Typography variant="body2">No Options</Typography>
                  </MenuItem>
                )
              ]}
        </MUISelect>
        {helperText && (
          <FormHelperText id={helperTextId} error={error}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    )
  }
)

export default Select
